/**
 * 内置技能面板 — 展示 platform 已启用技能。
 * 服务端统一处理分页、搜索、标签、分配状态过滤。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { skillService } from '../../services/skill';
import Tooltip from '../ui/Tooltip';

// 内置技能启用状态 — 内存缓存 + 数据库持久化
let _builtinEnabledCache: Record<string, boolean> = {};
let _assignedSlugsCache: string[] = [];

export function getBuiltinEnabledMap(): Record<string, boolean> {
  return _builtinEnabledCache;
}

export function getAssignedSkillSlugs(): string[] {
  return _assignedSlugsCache;
}

async function loadBuiltinEnabledFromServer(): Promise<Record<string, boolean>> {
  try {
    const result = await (window.electron.skills as any).getBuiltinEnabledState();
    _builtinEnabledCache = result?.data || {};
    return _builtinEnabledCache;
  } catch {
    return _builtinEnabledCache;
  }
}

async function saveBuiltinEnabledToServer(map: Record<string, boolean>) {
  _builtinEnabledCache = map;
  try {
    await (window.electron.skills as any).saveBuiltinEnabledState(map);
  } catch {
    // ignore
  }
}

type FilterTab = 'all' | 'assigned' | 'unassigned';

const PAGE_SIZE = 20;

const BuiltinSkillsPanel: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [filterTab, setFilterTab] = useState<FilterTab>('assigned');
  const [skills, setSkills] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [tags, setTags] = useState<{ tag: string; count: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>({});
  const [language, setLanguage] = useState(i18nService.getLanguage());

  const scrollRef = useRef<HTMLDivElement>(null);

  const categoryLabelMap: Record<string, string> = useMemo(() => ({
    '文档处理': i18nService.t('categoryDocuments'),
    '搜索资讯': i18nService.t('categorySearch'),
    '设计创作': i18nService.t('categoryDesign'),
    '效率工具': i18nService.t('categoryTools'),
    '写作助手': i18nService.t('categoryWriting'),
    '开发集成': i18nService.t('categoryDev'),
    'productivity': i18nService.t('categoryTools'),
    'development': i18nService.t('categoryDev'),
    'writing': i18nService.t('categoryWriting'),
    'analysis': i18nService.t('categoryAnalysis'),
    'design': i18nService.t('categoryDesign'),
    'education': i18nService.t('categoryEducation'),
    'other': i18nService.t('categoryOther'),
  }), [language]);

  const dateLocale = language === 'zh' ? 'zh-CN' : language === 'zh-TW' ? 'zh-TW' : 'en-US';

  const isSkillEnabled = (skillId: string) => enabledMap[skillId] !== false;

  const toggleSkillEnabled = async (skillId: string) => {
    const newMap = { ...enabledMap, [skillId]: !isSkillEnabled(skillId) };
    setEnabledMap(newMap);
    await saveBuiltinEnabledToServer(newMap);
  };

  // 初始加载启用状态 + 更新 slug 缓存
  useEffect(() => {
    loadBuiltinEnabledFromServer().then(setEnabledMap);
    // 更新 assigned slug 缓存（供 SkillsPopover 使用）
    skillService.getDistributedSkills().then((result) => {
      if (result.success && result.data) {
        const items = result.data?.data || result.data || [];
        if (Array.isArray(items)) {
          _assignedSlugsCache = items.map((ds: any) => ds.skill_slug || ds.skill_name || '').filter(Boolean);
        }
      }
    });
  }, []);

  useEffect(() => i18nService.subscribe(() => {
    setLanguage(i18nService.getLanguage());
    setPage(1);
    setSelectedCategory('all');
    setSkills([]);
  }), []);

  // 统一加载技能数据
  const loadSkills = useCallback(async (pageNum: number, append: boolean) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const params: any = { page: pageNum, pageSize: PAGE_SIZE, filter: filterTab };
      if (searchQuery) params.search = searchQuery;
      if (selectedCategory !== 'all') params.tag = selectedCategory;
      const result = await skillService.getAllBuiltinSkills(params);
      if (result.success && result.data) {
        const d = result.data?.data || result.data || {};
        // 兼容新格式 {items,total,tags} 和旧格式（纯数组）
        const items = Array.isArray(d) ? d : (d.items || []);
        const resTags = Array.isArray(d) ? [] : (d.tags || []);
        const resTotal = Array.isArray(d) ? d.length : (d.total || 0);

        if (append) {
          setSkills(prev => [...prev, ...items]);
        } else {
          setSkills(items);
        }
        setHasMore(items.length >= PAGE_SIZE && (append ? skills.length + items.length < resTotal : items.length < resTotal));
        if (resTags.length > 0) setTags(resTags);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filterTab, searchQuery, selectedCategory, language]);

  // 条件变化时重新加载
  useEffect(() => {
    setPage(1);
    setSkills([]);
    setHasMore(true);
    loadSkills(1, false);
  }, [searchQuery, selectedCategory, filterTab]);

  // 首次加载标签
  useEffect(() => {
    skillService.getAllBuiltinSkills({ page: 1, pageSize: 1 }).then((result) => {
      if (result.success && result.data) {
        const d = result.data?.data || result.data || {};
        if (!Array.isArray(d) && d.tags && Array.isArray(d.tags)) {
          setTags(d.tags);
        }
      }
    });
  }, [language]);

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 滚动加载更多
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !hasMore || loadingMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadSkills(nextPage, true);
    }
  }, [hasMore, loadingMore, loading, page, loadSkills]);

  const filterTabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'assigned', label: '已分配' },
    { key: 'unassigned', label: '未分配' },
  ];

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
          管理员为您所属部门分配的精选技能，沙箱执行时自动挂载
        </p>
      </div>

      {/* 搜索栏 */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
          <input
            type="text"
            placeholder={i18nService.t('searchSkills')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkText text-claude-text dark:placeholder-claude-darkTextSecondary placeholder-claude-textSecondary border dark:border-claude-darkBorder border-claude-border focus:outline-none focus:ring-2 focus:ring-claude-accent"
          />
        </div>
      </div>

      {/* 分配状态筛选 */}
      <div className="flex items-center gap-1">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setFilterTab(tab.key)}
            className={`px-2.5 py-1 text-xs rounded-lg whitespace-nowrap transition-colors ${
              filterTab === tab.key
                ? 'bg-claude-accent text-white'
                : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 分类标签 */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {tags.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-claude-accent text-white'
                  : 'dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkTextSecondary text-claude-textSecondary border dark:border-claude-darkBorder border-claude-border hover:border-claude-accent/50'
              }`}
            >
              {i18nService.t('allCategories')}
            </button>
            {tags.map((t) => (
              <button
                key={t.tag}
                type="button"
                onClick={() => setSelectedCategory(t.tag)}
                className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                  selectedCategory === t.tag
                    ? 'bg-claude-accent text-white'
                    : 'dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkTextSecondary text-claude-textSecondary border dark:border-claude-darkBorder border-claude-border hover:border-claude-accent/50'
                }`}
              >
                {categoryLabelMap[t.tag] || t.tag}
              </button>
            ))}
          </>
        )}
      </div>

      {/* 技能卡片网格 — 滚动加载 */}
      <div
        ref={scrollRef}
        className="overflow-y-auto max-h-[calc(100vh-320px)]"
        onScroll={handleScroll}
      >
        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            <div className="col-span-2 text-center py-8">
              <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-claude-accent border-r-transparent" />
            </div>
          ) : skills.length === 0 ? (
            <div className="col-span-2 text-center py-8 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {i18nService.t('skillsEmpty')}
            </div>
          ) : (
            skills.map((s: any) => {
              const name = s.display_name || s.slug || '';
              const summary = s.summary || '';
              const tag = s.tag || '';
              const skillId = s.slug || String(s.id);
              const enabled = isSkillEnabled(skillId);

              return (
                <div
                  key={s.id}
                  className={`rounded-xl border p-3 transition-colors ${
                    filterTab !== 'unassigned'
                      ? 'dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface/50 bg-claude-surface/50 hover:border-claude-accent/50'
                      : 'dark:border-claude-darkBorder/50 border-claude-border/50 dark:bg-claude-darkSurface/20 bg-claude-surface/20 opacity-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`text-sm font-medium truncate ${
                        filterTab !== 'unassigned' ? 'dark:text-claude-darkText text-claude-text' : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                      }`}>
                        {name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div
                        className={`w-9 h-5 rounded-full flex items-center transition-colors flex-shrink-0 ${
                          filterTab !== 'unassigned' ? 'cursor-pointer' : 'cursor-not-allowed'
                        } ${
                          enabled && filterTab !== 'unassigned'
                            ? 'bg-claude-accent'
                            : 'dark:bg-claude-darkBorder bg-claude-border'
                        }`}
                        onClick={() => filterTab !== 'unassigned' && toggleSkillEnabled(skillId)}
                      >
                        <div
                          className={`w-3.5 h-3.5 rounded-full bg-white shadow-md transform transition-transform ${
                            enabled && filterTab !== 'unassigned' ? 'translate-x-[18px]' : 'translate-x-[3px]'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <Tooltip
                    content={summary}
                    position="bottom"
                    maxWidth="360px"
                    className="block w-full"
                  >
                    <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary line-clamp-2 mb-2">
                      {summary || '暂无描述'}
                    </p>
                  </Tooltip>

                  <div className="flex items-center justify-between text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-600 dark:text-green-400 font-medium">
                        精选
                      </span>
                      <span>·</span>
                      <span>{s.created_at ? new Date(s.created_at).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) : '-'}</span>
                      {tag && (
                        <>
                          <span>·</span>
                          <span>{categoryLabelMap[tag] || tag}</span>
                        </>
                      )}
                    </div>
                    <span className={`px-1.5 py-0.5 rounded font-medium ${
                      filterTab !== 'unassigned'
                        ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-500/10 text-gray-500 dark:text-gray-400'
                    }`}>
                      {filterTab === 'unassigned' ? '未分配' : '已分配'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {loadingMore && (
          <div className="text-center py-4">
            <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-claude-accent border-r-transparent" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BuiltinSkillsPanel;
